import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { Language } from '../i18n/translations';
const BANNER_URL = 'https://cdn.magicpatterns.com/uploads/ay3vA9iB1cH2Mf867gqiAC/Banner1.1.png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/nrmTFaPw3fdgTnDyCxNNzV/Captura_de_pantalla_2026-05-26_231223.png';
export function LanguageSelector({
  onComplete
}: {
  onComplete: () => void;
}) {
  const {
    setLang
  } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const savedLang = localStorage.getItem('mp_lang');
    if (savedLang) {
      setIsVisible(false);
      onComplete();
    }
  }, [onComplete]);
  const handleSelect = (lang: Language) => {
    setLang(lang);
    setIsVisible(false);
    setTimeout(onComplete, 600); // Wait for fade out
  };
  if (!isVisible) return null;
  return <AnimatePresence data-id="element-415">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.6
    }} className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-brand-brown" data-id="element-416">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-417">
          <img src={BANNER_URL} alt="Background" className="w-full h-full object-cover" data-id="element-418" />
          <div className="absolute inset-0 bg-brand-brown/80 backdrop-blur-sm" data-id="element-419"></div>
        </div>

        <div className="relative z-10 flex flex-col items-center w-full max-w-4xl px-6" data-id="element-420">
          <motion.img initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} src={LOGO_URL} alt="Conexión" className="h-20 md:h-28 w-auto object-contain mb-12" data-id="element-421" />

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4,
          duration: 0.8
        }} className="text-center mb-12" data-id="element-422">
            <h1 className="font-serif text-4xl md:text-5xl text-brand-cream mb-4" data-id="element-423">
              Welcome · Bienvenidos
            </h1>
            <p className="text-brand-cream/80 text-lg md:text-xl tracking-wide" data-id="element-424">
              Select your language / Selecciona tu idioma
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl" data-id="element-425">
            {/* English Card */}
            <motion.button initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} whileHover={{
            scale: 1.03,
            y: -4
          }} whileTap={{
            scale: 0.98
          }} onClick={() => handleSelect('en')} className="group flex flex-col items-center justify-center bg-brand-cream/10 hover:bg-brand-cream/20 border border-brand-cream/20 hover:border-brand-terracotta p-8 rounded-xl backdrop-blur-md transition-all duration-300" data-id="element-426">
              <svg viewBox="0 0 60 40" className="w-16 h-auto rounded-sm shadow-md mb-6 transition-transform duration-300 group-hover:scale-110" data-id="element-427">
                <rect width="60" height="40" fill="#B22234" data-id="element-428" />
                <rect width="60" height="3.07" y="3.07" fill="#fff" data-id="element-429" />
                <rect width="60" height="3.07" y="9.23" fill="#fff" data-id="element-430" />
                <rect width="60" height="3.07" y="15.38" fill="#fff" data-id="element-431" />
                <rect width="60" height="3.07" y="21.53" fill="#fff" data-id="element-432" />
                <rect width="60" height="3.07" y="27.69" fill="#fff" data-id="element-433" />
                <rect width="60" height="3.07" y="33.84" fill="#fff" data-id="element-434" />
                <rect width="24" height="21.53" fill="#3C3B6E" data-id="element-435" />
                <circle cx="4" cy="4" r="1" fill="#fff" data-id="element-436" />
                <circle cx="12" cy="4" r="1" fill="#fff" data-id="element-437" />
                <circle cx="20" cy="4" r="1" fill="#fff" data-id="element-438" />
                <circle cx="8" cy="8" r="1" fill="#fff" data-id="element-439" />
                <circle cx="16" cy="8" r="1" fill="#fff" data-id="element-440" />
                <circle cx="4" cy="12" r="1" fill="#fff" data-id="element-441" />
                <circle cx="12" cy="12" r="1" fill="#fff" data-id="element-442" />
                <circle cx="20" cy="12" r="1" fill="#fff" data-id="element-443" />
                <circle cx="8" cy="16" r="1" fill="#fff" data-id="element-444" />
                <circle cx="16" cy="16" r="1" fill="#fff" data-id="element-445" />
              </svg>
              <span className="font-serif text-2xl text-brand-cream group-hover:text-brand-terracotta transition-colors" data-id="element-446">
                Continue in English
              </span>
            </motion.button>

            {/* Spanish Card */}
            <motion.button initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.7,
            duration: 0.6
          }} whileHover={{
            scale: 1.03,
            y: -4
          }} whileTap={{
            scale: 0.98
          }} onClick={() => handleSelect('es')} className="group flex flex-col items-center justify-center bg-brand-cream/10 hover:bg-brand-cream/20 border border-brand-cream/20 hover:border-brand-terracotta p-8 rounded-xl backdrop-blur-md transition-all duration-300" data-id="element-447">
              <svg viewBox="0 0 60 40" className="w-16 h-auto rounded-sm shadow-md mb-6 transition-transform duration-300 group-hover:scale-110" data-id="element-448">
                <rect width="20" height="40" fill="#006847" data-id="element-449" />
                <rect x="20" width="20" height="40" fill="#ffffff" data-id="element-450" />
                <rect x="40" width="20" height="40" fill="#ce1126" data-id="element-451" />
                <circle cx="30" cy="20" r="4" fill="#8b6508" data-id="element-452" />
              </svg>
              <span className="font-serif text-2xl text-brand-cream group-hover:text-brand-terracotta transition-colors" data-id="element-453">
                Continuar en Español
              </span>
            </motion.button>
          </div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1,
          duration: 0.8
        }} className="mt-12 text-brand-cream/50 text-sm text-center" data-id="element-454">
            You can change this anytime in the menu <br className="md:hidden" data-id="element-455" />
            <span className="hidden md:inline" data-id="element-456"> / </span>
            Puedes cambiarlo en cualquier momento
          </motion.p>
        </div>
      </motion.div>
    </AnimatePresence>;
}